<template>
  <div class="tab tab-general">
    <Toast
      title="Pour rappel :"
      text="L’évaluation des talents avec cette analyse ne remplace pas un processus complet, avec entretiens."
      color="yellow"
      icon="bi-info-circle"
      :closable="false"
    />
    <span v-if="campaignVersion?.duration !== 25">
      <h4>Graphique de positionnement</h4>
      <div class="graph-wrapper">
        <PositioningChart v-if="currentCandidate.participant_id" :modules="skills_evaluated"
          :candidate="currentCandidate" :campaign_scores="campaign_scores" />
      </div>
    </span>
    <template v-if="campaignStore.currentCampaign.version === 2">
      <h4>Les “scores” de ce talent</h4>
      <div class="skills-wrapper">
        <div
          v-for="skill in skills_evaluated"
          :key="skill.id"
          class="skill-element"
        >
          <span
            class="skill-notation"
            :class="
              currentCandidate.results[`${skill.id_v2 ?? skill.id}_score`].rank
            "
          >
            {{
              currentCandidate.results[`${skill.id_v2 ?? skill.id}_score`].rank
            }}
          </span>
          {{ skill.name }}
        </div>
      </div>
      <!--<template v-if="campaignStore.currentCampaign.version === 2">
        <h4>Désirabilité sociale ?</h4>
        <Tag class="yellow" :text="social_desire_obj?.label ?? ''" />
        <p>{{ social_desire_obj?.text }}</p>
        <Toast
          text="La désirabilité sociale s’applique surtout aux compétences suivantes : empathie, écoute active et confiance en ses capacités."
          color="yellow"
          :closable="false"
          :icon="null"
        />
      </template>-->
      <template v-if="currentLeadershipStrategy">
        <div>
          <h4 style="margin-bottom: 20px">Leadership</h4>
          <div class="styles-wrapper">
            <div class="style-element">
              <Tag
                class="yellow"
                :text="currentLeadershipStrategy.label ?? ''"
              />
            </div>
          </div>
          {{ currentLeadershipStrategy.analysis ?? "" }}
        </div>
      </template>
      <template v-if="currentLearningStrategy">
        <div>
          <h4 style="margin-bottom: 20px">Stratégie d'apprentissage</h4>
          <div class="styles-wrapper">
            <div class="style-element">
              <Tag
                class="yellow"
                :text="currentLearningStrategy.label ?? ''"
              />
            </div>
          </div>
          {{ currentLearningStrategy.analysis ?? "" }}
        </div>
      </template>
      <h4>Ce qui motive ce talent</h4>
      <Tag class="yellow" :text="motivation_obj?.label ?? ''" />
      <p>{{ motivation_obj?.analysis }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { useCampaignStore } from "@/stores/campaign.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useModulesStore } from "@/stores/modules.js";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import PositioningChart from "@/components/charts/custom/PositioningChart.vue";

import type { LeadershipStyle } from "@/views/game/resultV2/leadership_style"

const route = useRoute();

const campaignStore =
  route.name === "campaign" ? useCampaignStore() : useOnboardingStore();

const modulesStore = useModulesStore();

const { modules } = storeToRefs(modulesStore);

const props = defineProps({
  currentCandidate: {
    type: Object,
    required: true,
  },
});

const state = reactive({
  social_desire_data: [
    {
      score: 75,
      label: "Forte désirabilité sociale",
      text: "Les questionnaires montrent une forte tendance à la désirabilité sociale chez ce talent.\nCette tendance suggère une forte inclination à présenter une image positive de soi, compromettant la validité des données recueillies. Il est recommandé de compléter cette évaluation par d’autres méthodes pour obtenir une évaluation plus précise et holistique des compétences et attitudes du talent.",
    },
    {
      score: 50,
      label: "Désirabilité sociale moyenne",
      text: "Les questionnaires révèlent une tendance modérée à la désirabilité sociale chez ce talent.\nCette tendance suggère une certaine inclination à présenter une image positive de soi, ce qui nécessite une interprétation prudente des résultats pour garantir une évaluation précise des compétences et attitudes du talent.",
    },
    {
      score: 25,
      label: "Faible désirabilité sociale",
      text: "Les questionnaires indiquent une faible tendance à la désirabilité sociale chez ce talent. \nCette tendance suggère une légère inclination à présenter une image positive de soi, mais les réponses restent globalement fiables et représentatives des compétences réelles du talent.",
    },
    {
      score: 0,
      label: "Pas de désirabilité sociale",
      text: "Les questionnaires n’indiquent aucune tendance à la désirabilité sociale chez ce talent.\nLes réponses sont fiables et représentatives des compétences réelles du talent.",
    },
  ],
});

const skills_evaluated = computed(() => {
  if (!props.currentCandidate.results) return [];

  return modules.value.filter((elt) => {
    if (campaignStore.currentCampaign.version === 1) {
      return Object.keys(props.currentCandidate.results).includes(elt.id);
    }

    if (elt.disabled) return false;

    if (!Object.keys(props.currentCandidate.results).includes(`${elt.id_v2 ?? elt.id}_score`)) return false;

    const currentCandidateResults = props.currentCandidate.results[`${elt.id_v2 ?? elt.id}_${elt.type === "skill" ? "score" : "style"}`];
    const hasValue = currentCandidateResults.value || currentCandidateResults.rank;
    if (!hasValue) return false;

    if (elt.id === "leadership") return campaignStore.isV2_1(campaignStore.currentCampaign);
    if (elt.id === "planning") return campaignStore.isV2_1(campaignStore.currentCampaign);

    return true;
  });
});

const social_desire_obj = computed(() => {
  if (!props.currentCandidate.results) return null;

  const social_desire_score =
    props.currentCandidate.results.social_desire_score?.score;

  return state.social_desire_data.find(
    (obj) => social_desire_score > obj.score
  );
});

const motivation_obj = computed(() => {
  if (!props.currentCandidate.results) return null;

  const motivation_style = props.currentCandidate.results.motivation_style;

    // Get all the learn strategies
  const motivations = modules.value.find((elt) => elt.id === "motivation");

  return motivations.result_wordings?.[motivation_style];
});

const styles_evaluated = computed(() => {
  if (!props.currentCandidate.results) return [];

  return modules.value.filter((elt) => {
    return (
      Object.keys(props.currentCandidate.results).includes(`${elt.id}_style`) &&
      !elt.disabled
    );
  });
});

const currentLearningStrategy = computed(() => {
  if (!props.currentCandidate.results) return null;

  // Get all the learn strategies
  const learn_strategies = Object.values(modules.value).find((elt) => elt.id === "learn_strategy");

  // Get the strategy of the current candidate
  const strategy = Object.values(learn_strategies.result_wordings).find((elt) => elt.id === props.currentCandidate.results.learn_strategy_style?.value);

  return strategy;
});

const currentLeadershipStrategy = computed(() => {
  if (!props.currentCandidate.results || !props.currentCandidate.results.leadership_style?.value) return null;

  const leadership_strategies = Object.values(modules.value).find((elt) => elt.id === "leadership");

  const leadership_styles: LeadershipStyle = JSON.parse(props.currentCandidate.results.leadership_style.value);

  return leadership_strategies.result_wordings?.[leadership_styles.leading_style];
});

const campaign_scores = computed(() => {
  if (!props.currentCandidate.results) return [];

  return campaignStore.currentCampaign.averageScores;
});

const campaignCalibrationID = computed(() => {
  if (!campaignStore.currentCampaign) return null;

  return campaignStore.currentCampaign.calibration_id;
});

const campaignVersion = computed<CampaignVersion | null>(() => {
  return getVersionObjectFromVersionID(campaignCalibrationID.value);
});
</script>
